import React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Camera, FileText, X } from "lucide-react";

export interface PersonEntry {
  gesellschaft: string;
  kopie: boolean;
  foto: boolean;
  keineVorhanden: boolean;
}

export interface MultiPersonTableValue {
  gp1?: PersonEntry[];
  gp2?: PersonEntry[];
}

interface MultiPersonTableProps {
  label: string;
  value: MultiPersonTableValue;
  onChange: (value: MultiPersonTableValue) => void;
  showGP2?: boolean;
  maxEntries?: number;
  className?: string;
}

const emptyEntry = (): PersonEntry => ({
  gesellschaft: "",
  kopie: false,
  foto: false,
  keineVorhanden: false,
});

export function MultiPersonTable({
  label,
  value = { gp1: [], gp2: [] },
  onChange,
  showGP2 = true,
  maxEntries = 5,
  className,
}: MultiPersonTableProps) {
  const gp1Entries = value.gp1 || [];
  const gp2Entries = value.gp2 || [];

  const updateGP = (
    gp: "gp1" | "gp2",
    index: number,
    field: keyof PersonEntry,
    fieldValue: string | boolean
  ) => {
    const entries = gp === "gp1" ? [...gp1Entries] : [...gp2Entries];
    
    if (field === "keineVorhanden" && fieldValue === true) {
      entries[index] = { ...entries[index], kopie: false, foto: false, keineVorhanden: true };
    } else if ((field === "kopie" || field === "foto") && fieldValue === true) {
      entries[index] = { ...entries[index], [field]: true, keineVorhanden: false };
    } else {
      entries[index] = { ...entries[index], [field]: fieldValue };
    }

    onChange({
      ...value,
      [gp]: entries,
    });
  };

  const addEntry = (gp: "gp1" | "gp2") => {
    const entries = gp === "gp1" ? [...gp1Entries] : [...gp2Entries];
    if (entries.length < maxEntries) {
      onChange({
        ...value,
        [gp]: [...entries, emptyEntry()],
      });
    }
  };

  const removeEntry = (gp: "gp1" | "gp2", index: number) => {
    const entries = gp === "gp1" ? [...gp1Entries] : [...gp2Entries];
    entries.splice(index, 1);
    onChange({
      ...value,
      [gp]: entries,
    });
  };

  const renderPersonColumn = (
    title: string,
    gp: "gp1" | "gp2",
    entries: PersonEntry[]
  ) => (
    <div className="flex-1 space-y-3">
      <Label className="text-sm font-medium">{title}</Label>
      
      {entries.map((entry, index) => (
        <div
          key={index}
          className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg"
        >
          <Input
            value={entry.gesellschaft}
            onChange={(e) => updateGP(gp, index, "gesellschaft", e.target.value)}
            placeholder="Gesellschaft"
            className="flex-1 h-8"
          />
          
          <Button
            type="button"
            size="sm"
            variant={entry.kopie ? "default" : "outline"}
            className="h-8 w-8 p-0"
            onClick={() => updateGP(gp, index, "kopie", !entry.kopie)}
            title="Kopie"
          >
            <FileText className="h-4 w-4" />
          </Button>
          
          <Button
            type="button"
            size="sm"
            variant={entry.foto ? "default" : "outline"}
            className="h-8 w-8 p-0"
            onClick={() => updateGP(gp, index, "foto", !entry.foto)}
            title="Foto"
          >
            <Camera className="h-4 w-4" />
          </Button>
          
          <Button
            type="button"
            size="sm"
            variant={entry.keineVorhanden ? "destructive" : "outline"}
            className="h-8 text-xs px-2"
            onClick={() => updateGP(gp, index, "keineVorhanden", !entry.keineVorhanden)}
          >
            keine
          </Button>
          
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
            onClick={() => removeEntry(gp, index)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      
      {entries.length < maxEntries && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addEntry(gp)}
          className="w-full"
        >
          + Hinzufügen
        </Button>
      )}
    </div>
  );

  return (
    <div className={cn("space-y-4", className)}>
      <h4 className="font-medium text-sm text-muted-foreground">{label}</h4>
      
      <div className={cn("grid gap-4", showGP2 ? "grid-cols-2" : "grid-cols-1")}>
        {renderPersonColumn("GP 1", "gp1", gp1Entries)}
        {showGP2 && renderPersonColumn("GP 2", "gp2", gp2Entries)}
      </div>
    </div>
  );
}
